import type { EmailCopy, EmailTypeSetting } from '../../types/email';

/** Mirror of backend services/email/emailCopy: limits, the school's words over EduBook's, and placeholder checks. */

export type CopyField = keyof EmailCopy;

export const COPY_LIMITS: Record<CopyField, number> = { subject: 150, heading: 150, body: 2000, button: 40 };

const norm = (t: string) => t.trim().replace(/\s+/g, ' ').toLowerCase();

/** The words the school sends now: its own where it changed them, else EduBook's. */
export const currentCopy = (t: EmailTypeSetting): EmailCopy => {
  const pick = (k: CopyField, fallback: string) => t.copy[k]?.trim() || fallback;
  const { subject, heading, body, button } = t.defaults;
  return {
    subject: pick('subject', subject),
    heading: pick('heading', heading),
    body: pick('body', body),
    ...(button !== undefined ? { button: pick('button', button) } : {}),
  };
};

export const isEdited = (t: EmailTypeSetting) => Object.values(t.copy).some(v => !!v?.trim());

/** Placeholders in `text` this email doesn't have, as typed. */
export const unknownPlaceholders = (text: string, t: EmailTypeSetting): string[] => {
  const known = new Set(t.placeholders.map(p => norm(p.token)));
  return Array.from(text.matchAll(/\{([^{}]+)\}/g)).map(m => m[1]).filter(tok => !known.has(norm(tok)));
};

export const sameCopy = (a: EmailCopy, b: EmailCopy) =>
  (Object.keys(a) as CopyField[]).every(k => (a[k] ?? '').trim() === (b[k] ?? '').trim());

/** What's wrong with each field, if anything: empty, or a placeholder the email doesn't have. */
export const copyErrors = (t: EmailTypeSetting, value: EmailCopy): Partial<Record<CopyField, string>> => {
  const out: Partial<Record<CopyField, string>> = {};
  for (const k of Object.keys(t.defaults) as CopyField[]) {
    const v = value[k] ?? '';
    const unknown = unknownPlaceholders(v, t)[0];
    if (!v.trim()) out[k] = 'Can’t be empty. Reset to use EduBook’s wording.';
    else if (unknown !== undefined) out[k] = `{${unknown}} isn’t a placeholder in this email.`;
  }
  return out;
};
